import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';

import type {
  FundHolder,
  TopFundHolders,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DisclosureStatementButton } from '#/components/content/DisclosureStatement';
import { DataTable } from '#/components/data/DataTable';
import { NoData } from '#/components/ui/NoData';
import { RichText } from '#/components/ui/RichText';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { Ticker } from '#/components/ui/Ticker';
import type { ProductTypeSlug } from '#/types';
import { DASH } from '#/utils/constants';
import {
  formatInteger,
  formatMonthDayYearDate,
  formatPercent,
} from '#/utils/format';

const HOLDER_TYPE_CATEGORY_SLUGS: Record<
  NonNullable<FundHolder['holder_type']>,
  ProductTypeSlug
> = {
  etf: 'etfs',
  mutual_fund: 'mutual-funds',
};

const columns: ColumnDef<FundHolder>[] = [
  {
    id: 'holder',
    accessorKey: 'title',
    header: 'Holder',
    enableSorting: false,
    size: 300,
    minSize: 150,
    maxSize: 300,
    cell: ({ row }) => {
      const wrapperClasses = 'flow flow-0 inline-flex items-center gap-8';
      const title = row.original.title ?? DASH;
      const category = row.original.holder_type
        ? HOLDER_TYPE_CATEGORY_SLUGS[row.original.holder_type]
        : null;

      if (!row.original.fund_ticker || !category) {
        return (
          <span className={wrapperClasses}>
            <Ticker symbol={row.original.fund_ticker ?? DASH} size="sm" />
            <span className="text-pretty">{title}</span>
          </span>
        );
      }

      return (
        <span className={wrapperClasses}>
          <Ticker symbol={row.original.fund_ticker ?? DASH} size="sm" />
          <Link
            to="/$category/$id"
            params={{ category, id: row.original.fund_ticker }}
            data-track="detail_top_holder"
            data-track-label={row.original.title ?? undefined}
            data-track-context="top_holders"
            className="text-pretty"
          >
            {title}
          </Link>
        </span>
      );
    },
  },
  {
    accessorKey: 'current_shares',
    header: 'Current Shares',
    meta: { align: 'end' },
    cell: ({ getValue }) =>
      formatInteger(getValue<number | null>()) ?? <NoData />,
  },
  {
    accessorKey: 'date_reported',
    header: 'Date Reported',
    meta: { align: 'end' },
    cell: ({ getValue }) =>
      formatMonthDayYearDate(getValue<string | null>()) ?? <NoData />,
  },
  {
    accessorKey: 'percent_total_shares_held',
    header: 'Total Shares Held',
    meta: { align: 'end' },
    cell: ({ getValue }) =>
      formatPercent(getValue<number | null>()) ?? <NoData />,
  },
  {
    accessorKey: 'percent_total_assets',
    header: 'Total Assets',
    meta: { align: 'end' },
    cell: ({ getValue }) =>
      formatPercent(getValue<number | null>()) ?? <NoData />,
  },
];

interface EquityTopHoldersSectionProps {
  data: TopFundHolders | null | undefined;
  intro?: string;
}

export function EquityTopHoldersSection({
  data,
  intro,
}: EquityTopHoldersSectionProps) {
  const holders = data?.fund_holders;
  if (!holders || holders.length === 0) {
    return null;
  }

  return (
    <section
      id="top-holders"
      className="flow flow-inner-8 scroll-mt-(--scroll-offset)"
    >
      <SectionHeader
        title="Top Fund Holders"
        actionAlignment="center"
        actions={<DisclosureStatementButton />}
      />

      {intro && (
        <RichText html={intro} className="text-fluid-base-sm text-fg" />
      )}

      <div className="flow-32 p-gap-inset border-divider-subtle -mx-gap border-t border-b sm:mx-0 sm:rounded sm:border">
        <DataTable
          aria-label="Top fund holders"
          columns={columns}
          data={holders}
          initialSorting={[{ id: 'current_shares', desc: true }]}
          trackContext="top_holders"
        />
      </div>
    </section>
  );
}
